import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { api, naira, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { NIGERIAN_STATES } from "@/lib/constants";
import { API } from "@/lib/api";
import { Loader2, Landmark, RotateCcw, Layers, ArrowRight, Download } from "lucide-react";
import { toast } from "sonner";

const FLOW = ["pending_approval", "approved", "processing", "paid", "reconciled"];
const nextLabel = (s) => ({ pending_approval: "Approve", approved: "Start Processing", processing: "Mark Paid", paid: "Reconcile" }[s]);
const badge = (s) => ({ pending_approval: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800", processing: "bg-blue-100 text-blue-800", paid: "bg-emerald-100 text-emerald-800", reconciled: "bg-gray-200 text-gray-700" }[s] || "bg-gray-100 text-gray-700");

export default function FinancePortal() {
  const [settlements, setSettlements] = useState([]);
  const [batches, setBatches] = useState([]);
  const [reversals, setReversals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [scopeOpen, setScopeOpen] = useState(false);
  const [scope, setScope] = useState({ vendor_id: "", state: "", date_from: "", date_to: "" });
  const [detail, setDetail] = useState(null);
  const openDetail = (b) => api.get(`/finance/settlement-batches/${b.id}`).then((r) => setDetail(r.data)).catch((e) => toast.error(formatApiError(e)));

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/settlements").then((r) => setSettlements(r.data.settlements)),
      api.get("/finance/settlement-batches").then((r) => setBatches(r.data.batches)),
      api.get("/admin/reversals").then((r) => setReversals(r.data.reversals)),
    ]).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const createBatch = async () => {
    setBusy(true);
    try {
      const payload = {
        vendor_id: scope.vendor_id && scope.vendor_id !== "__all__" ? scope.vendor_id : null,
        state: scope.state && scope.state !== "__all__" ? scope.state : null,
        date_from: scope.date_from || null,
        date_to: scope.date_to || null,
      };
      const { data } = await api.post("/finance/settlement-batches", payload);
      toast.success(`Batch ${data.ref} created · ${data.transaction_count} txns · ${naira(data.total_amount)}`);
      setScopeOpen(false); setScope({ vendor_id: "", state: "", date_from: "", date_to: "" }); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const exportBatch = async (b) => {
    try {
      const res = await api.get(`/finance/settlement-batches/${b.id}/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = `${b.ref}-schedule.csv`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${b.ref} schedule downloaded`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const advance = async (b) => {
    try { const { data } = await api.post(`/finance/settlement-batches/${b.id}/advance`); toast.success(`${data.ref} → ${data.status.replace("_", " ")}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const totalPending = settlements.reduce((s, r) => s + r.pending, 0);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>;
  return (
    <div>
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-10">
        <span className="text-xs font-mono uppercase tracking-widest text-emerald-700">Finance Officer</span>
        <h1 className="mt-2 font-display font-bold text-3xl text-emerald-950 flex items-center gap-2"><Landmark className="w-6 h-6 text-emerald-600" /> Settlements & Reversals</h1>

        <Tabs defaultValue="batches" className="mt-8">
          <TabsList className="bg-emerald-50">
            <TabsTrigger data-testid="fin-tab-batches" value="batches">Settlement Batches</TabsTrigger>
            <TabsTrigger data-testid="fin-tab-pending" value="pending">Pending by Vendor</TabsTrigger>
            <TabsTrigger data-testid="fin-tab-reversals" value="reversals">Reversals</TabsTrigger>
          </TabsList>

          <TabsContent value="batches" className="mt-4">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-gray-600">Group pending vendor payouts — all, or filtered by vendor, state or date — into an approvable batch with a status trail.</p>
              <Button data-testid="create-batch-btn" onClick={() => setScopeOpen(true)} disabled={totalPending === 0} className="bg-emerald-600 hover:bg-emerald-700"><Layers className="w-4 h-4 mr-2" />{totalPending === 0 ? "Nothing pending" : `New Batch (${naira(totalPending)})`}</Button>
            </div>
            <div className="space-y-4">
              {batches.length === 0 && <div className="text-center text-gray-500 py-14 border border-dashed border-emerald-200 rounded-2xl">No settlement batches yet.</div>}
              {batches.map((b) => (
                <div key={b.id} data-testid={`batch-${b.ref}`} className="rounded-2xl border border-emerald-100 bg-white p-5">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <div className="font-mono font-bold text-emerald-800">{b.ref}</div>
                      <div className="text-sm text-gray-600">{b.vendor_count} vendor{b.vendor_count === 1 ? "" : "s"} · {b.transaction_count} transaction{b.transaction_count === 1 ? "" : "s"} · <span className="font-semibold text-emerald-700">{naira(b.total_amount)}</span></div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={badge(b.status)}>{b.status.replace("_", " ")}</Badge>
                      <Button data-testid={`view-${b.ref}`} size="sm" variant="ghost" className="text-emerald-800" onClick={() => openDetail(b)}>View</Button>
                      <Button data-testid={`export-${b.ref}`} size="sm" variant="outline" onClick={() => exportBatch(b)} className="border-emerald-200 text-emerald-800"><Download className="w-3.5 h-3.5 mr-1" />CSV</Button>
                      {b.status !== "reconciled" && <Button data-testid={`advance-${b.ref}`} size="sm" onClick={() => advance(b)} className="bg-emerald-600 hover:bg-emerald-700">{nextLabel(b.status)} <ArrowRight className="w-3.5 h-3.5 ml-1" /></Button>}
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    {FLOW.map((s, i) => (
                      <div key={s} className="flex items-center gap-2">
                        <span className={`text-[11px] px-2 py-0.5 rounded-full ${FLOW.indexOf(b.status) >= i ? "bg-emerald-600 text-white" : "bg-gray-100 text-gray-400"}`}>{s.replace("_", " ")}</span>
                        {i < FLOW.length - 1 && <span className="text-gray-300">→</span>}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="pending" className="mt-4">
            <div className="rounded-2xl border border-emerald-100 bg-white overflow-x-auto"><Table>
              <TableHeader><TableRow className="bg-emerald-50/60"><TableHead>Vendor</TableHead><TableHead>ID</TableHead><TableHead>Txns</TableHead><TableHead>Pending</TableHead><TableHead>Settled</TableHead></TableRow></TableHeader>
              <TableBody>{settlements.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center text-gray-500 py-10">Nothing pending.</TableCell></TableRow> : settlements.map((s) => (
                <TableRow key={s.vendor_id}><TableCell>{s.vendor_name}</TableCell><TableCell className="font-mono text-xs">{s.vendor_id}</TableCell><TableCell>{s.count}</TableCell><TableCell className="text-amber-700 font-semibold">{naira(s.pending)}</TableCell><TableCell className="text-gray-500">{naira(s.settled)}</TableCell></TableRow>
              ))}</TableBody>
            </Table></div>
          </TabsContent>

          <TabsContent value="reversals" className="mt-4">
            <div className="rounded-2xl border border-emerald-100 bg-white overflow-x-auto"><Table>
              <TableHeader><TableRow className="bg-emerald-50/60"><TableHead>Reversal ID</TableHead><TableHead>Original</TableHead><TableHead>TMF ID</TableHead><TableHead>Amount</TableHead><TableHead>Reason</TableHead><TableHead>By</TableHead></TableRow></TableHeader>
              <TableBody>{reversals.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center text-gray-500 py-10">No reversals.</TableCell></TableRow> : reversals.map((r) => (
                <TableRow key={r.id}><TableCell className="font-mono text-xs">{r.txn_id}</TableCell><TableCell className="font-mono text-xs">{r.original_txn}</TableCell><TableCell className="font-mono text-xs">{r.tmf_id}</TableCell><TableCell className="font-semibold text-blue-600">{naira(r.amount)}</TableCell><TableCell className="text-xs">{r.reason || "—"}</TableCell><TableCell className="text-xs">{r.authorised_by || "—"}</TableCell></TableRow>
              ))}</TableBody>
            </Table></div>
          </TabsContent>
        </Tabs>

        <Dialog open={scopeOpen} onOpenChange={setScopeOpen}>          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Create Settlement Batch</DialogTitle></DialogHeader>
            <p className="text-sm text-gray-600">Leave filters blank to batch all pending payouts, or narrow the scope.</p>
            <div className="space-y-3 mt-2">
              <div>
                <Label>Vendor (optional)</Label>
                <Select value={scope.vendor_id} onValueChange={(v) => setScope({ ...scope, vendor_id: v })}>
                  <SelectTrigger data-testid="batch-vendor" className="mt-1.5 h-10"><SelectValue placeholder="All vendors" /></SelectTrigger>
                  <SelectContent><SelectItem value="__all__">All vendors</SelectItem>{settlements.map((s) => <SelectItem key={s.vendor_id} value={s.vendor_id}>{s.vendor_name || s.vendor_id}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>State (optional)</Label>
                <Select value={scope.state} onValueChange={(v) => setScope({ ...scope, state: v })}>
                  <SelectTrigger data-testid="batch-state" className="mt-1.5 h-10"><SelectValue placeholder="All states" /></SelectTrigger>
                  <SelectContent><SelectItem value="__all__">All states</SelectItem>{NIGERIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>From</Label><Input data-testid="batch-from" type="date" value={scope.date_from} onChange={(e) => setScope({ ...scope, date_from: e.target.value })} className="mt-1.5 h-10" /></div>
                <div><Label>To</Label><Input data-testid="batch-to" type="date" value={scope.date_to} onChange={(e) => setScope({ ...scope, date_to: e.target.value })} className="mt-1.5 h-10" /></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setScopeOpen(false)}>Cancel</Button>
              <Button data-testid="batch-create-confirm" onClick={createBatch} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Batch"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{detail?.batch?.ref} · {detail?.batch?.status?.replace("_", " ")}</DialogTitle></DialogHeader>
            <div className="text-sm text-gray-600">{detail?.batch?.transaction_count} transactions · {detail && naira(detail.batch.total_amount)}</div>
            <h4 className="font-semibold text-sm mt-3 text-emerald-950">Status Trail</h4>
            <div className="space-y-1">
              {detail?.batch?.history?.map((h, i) => (
                <div key={i} className="flex items-center justify-between text-xs border-b border-gray-100 py-1">
                  <span className="capitalize font-medium text-emerald-800">{h.status.replace("_", " ")} <span className="text-gray-400">by {h.by}</span></span>
                  <span className="font-mono text-gray-400">{new Date(h.at).toLocaleString()}</span>
                </div>
              ))}
            </div>
            <h4 className="font-semibold text-sm mt-4 text-emerald-950">Transactions</h4>
            <div className="rounded-xl border border-emerald-100 overflow-x-auto"><Table>
              <TableHeader><TableRow className="bg-emerald-50/60"><TableHead>Txn ID</TableHead><TableHead>TMF ID</TableHead><TableHead>Vendor</TableHead><TableHead>Amount</TableHead></TableRow></TableHeader>
              <TableBody>{detail?.transactions?.map((t) => (
                <TableRow key={t.id}><TableCell className="font-mono text-xs">{t.txn_id}</TableCell><TableCell className="font-mono text-xs">{t.tmf_id}</TableCell><TableCell className="text-xs">{t.vendor_name}</TableCell><TableCell className="font-semibold">{naira(t.amount)}</TableCell></TableRow>
              ))}</TableBody>
            </Table></div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
